import React, { useEffect, useCallback, useState, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import WindowHeightComponent from "../../Layouts/WindowHeight";
import SectionHeader from "./SectionHeader";
import MainLayout from "../../Layouts/MainLayout";
import { User } from "../types/User";
import { service } from "../../../services/AxiosServices";
import { toast } from "react-toastify";
import Pluseicon from "../../static/svg/Pluseicon";
import UserModal from "./UserModal";
import x from "../../static/images/x.png";
import Multiselect from "multiselect-react-dropdown";
import Label from "../atoms/Label";
import axios from "axios";

interface Member {
  user_id: number;
  first_name: string;
  last_name: string;
}

interface PartnerCompanyData {
  company_id: number;
  company_name: string;
  members: Member[];
  logo: File | null; 
}

export const PartnerTab = () => {
  
  const location = useLocation();
  const [formData, setFormData] = useState<PartnerCompanyData>({
    company_id: 0,
    company_name: "",
    members: [],
    logo: null,
  });
  
  const [logoError, setLogoError] = useState("");
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const navigate = useNavigate();
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [loadSave, setLoadSave] = useState(false);
  const [editUser, setEditUser] = useState<User | null>(null);
  const handleAddUser = () => setShowAddUserModal(true);
  const [allUsers, setAllUsers] = useState<{ id: number; name: string }[]>([]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [serverError, setServerError] = useState<string>("");



  useEffect(() => {
    fetchData();
    const searchParams = new URLSearchParams(location.search);
    const id = searchParams.get("id");
    if (id) {
      fetchPartnerCompany(id);
    }
  }, [location.search]);

  // API call for fetch by company id
  const fetchPartnerCompany = async (companyId: string) => {
    try {
      const [partnerResponse, allUsers] = await Promise.all([
        service('/manage-partners', "post", { id: companyId }),
        fetchData()
      ]);
      
      const data = (partnerResponse.data as PartnerCompanyData[])[0];
      setFormData({
        company_id: data?.company_id,
        company_name: data?.company_name || "",
        members: data?.members,
        logo: data.logo ?? null,
      });
      if (typeof data.logo === "string") {
        setLogoPreview(data.logo);
      }
  
    } catch (err) {
      console.error("Error fetching partner company:", err);
      toast.error("Failed to load partner details.");
    }
  };
  

  // API call for fetch all members 
  async function fetchData() {
    try {
      const response = await service<{ partner_members: any[] }>("/partner-list", "post");
      const users = response.data.partner_members.map((user: any) => ({
        id: user.user_id,
        name: `${(user.first_name || "").trim()} ${(user.last_name || "").trim()}`.trim(),
      }))
      .sort((a, b) => a.name.localeCompare(b.name)); 
    
      setAllUsers(users);
      return users;
    } catch (err) {
      console.error(err);
      return [];
    }
  }
  const handleCompanyNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, company_name: e.target.value });
  };
  const validateForm = () => {
    let newErrors: { [key: string]: string } = {};

    const companyNameTrimmed = formData.company_name.trim();

    if (!companyNameTrimmed) {
      newErrors.companyName = "Company name is required.";
    } else if (companyNameTrimmed.length > 255) {
      newErrors.companyName = "Title field will accept only 255 characters.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
  
    const isValid = validateForm();
    if (!isValid) {
      setServerError("");
      return;
    }
    if (logoError) {
      return;
    }
    try {
      setLoadSave(true);
      const isUpdate = !!formData.company_id;
      const formDataToSend = new FormData();
  
      if (isUpdate) {
        formDataToSend.append("companyId", String(formData?.company_id));
      }
  
      formDataToSend.append("companyName", formData?.company_name.trim());
  

      const userIds = formData.members.map(member => Number(member.user_id));
      userIds.forEach((id, index) => {
        formDataToSend.append(`associatedUsers`, id.toString());
      });
      if (formData.logo) {
        formDataToSend.append("logo", formData.logo);
      }
      formDataToSend.forEach((item, index) => {
        console.log(`Item ${index}: ${item}`);
        
      });
      
      await service(
        "/create-partner-company", 
        "post", 
        formDataToSend, 
        "multipart/form-data"
      );
  
      toast.success(
        isUpdate
          ? "Partner company updated successfully!"
          : "Partner company saved successfully!",
        { autoClose: 2000 }
      );
      navigate("/manage-partner-company")
    } catch (err: any) {
      console.error("Error saving partner:", err);
      const errorMessage =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        "Something went wrong. Please try again.";
  
      setServerError(errorMessage);
    }finally{
      setLoadSave(false);
    }
  };
  const handleCancel = () => {
    navigate("/manage-partner-company");
  };
  const handleCloseModal = () => {
    setEditUser(null);
    setShowAddUserModal(false);
  };
  const handleSaveNewUser = async (newUser: User) => {
    fetchData();
    setShowAddUserModal(false);
  };
  const handleUserSelect = useCallback((selectedList: any[]) => {
    const selectedMembers = selectedList.map(user => {
      const [firstName, ...lastName] = user.name.split(" ");
      return {
        user_id: user.id,
        first_name: firstName || "",
        last_name: lastName.join(" ") || ""
      };
    });
    
    setFormData(prev => ({
      ...prev,
      members: selectedMembers
    }));
  }, []);
  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
  
    if (!file) {
      setFormData((prev) => ({ ...prev, logo: null }));
      setLogoPreview(null);
      setLogoError("");
      return;
    }
  
    const validTypes = ["image/png", "image/jpeg", "image/jpg", "image/gif"];
    const maxSize = 2 * 1024 * 1024; // 2MB
  
    if (!validTypes.includes(file.type)) {
      setLogoError("Invalid file format, please upload only .png, .jpeg, and .gif file formats.");
      setFormData((prev) => ({ ...prev, logo: null }));
      setLogoPreview(null);
      return;
    }
  
    if (file.size > maxSize) {
      setLogoError("File size is greater than 2MB.");
      setFormData((prev) => ({ ...prev, logo: null }));
      setLogoPreview(null);
      return;
    }
  
    setFormData((prev) => ({ ...prev, logo: file }));
    setLogoPreview(URL.createObjectURL(file));
    setLogoError("");
  };
  const handleRemoveLogo = () => {
    setFormData(prev => ({ ...prev, logo: null }));
    setLogoPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  const handleUserRemove = useCallback((selectedList: any[]) => {
    const remainingMembers = selectedList.map(user => {
      const [firstName, ...lastName] = user.name.split(" ");
      return {
        user_id: user.id,
        first_name: firstName || "",
        last_name: lastName.join(" ") || ""
      };
    });
    
    setFormData(prev => ({
      ...prev,
      members: remainingMembers
    }));
  }, []);
  
  return (
    <MainLayout excludeSidebar={true}>
      <div className="row ms-1">
        <SectionHeader back={true} title={formData.company_id ?"Edit partner":"Create partner"} />
      </div>
      <WindowHeightComponent className="ms-3">
        <div className="p-3 w-100">
          {serverError && (
            <li className="alert alert-danger">{serverError}</li>
          )}
          {Object.keys(errors).length > 0 && (
            <div className="alert alert-danger">
              <ul>
                {Object.values(errors).map((err, index) => (
                  <li key={index}>{err}</li>
                ))}
              </ul>
            </div>
          )}
          <form onSubmit={handleSave}>
            <div className="row">
              <div className="col-6 mb-3">
                <Label htmlFor="Partner company"className="form-label" required>Partner company:</Label>
                <input
                  type="text"
                  className="form-control"
                  name="company_name"
                  value={formData.company_name}
                  onChange={handleCompanyNameChange}
                />
              </div>

              <div className="col-6 mb-3">
                <div>
                  <Label htmlFor="Members" className="form-label">Members</Label>
                </div>
                <div className="d-flex align-items-center">
                  <div className="flex-1">
                    <Multiselect
                      options={allUsers}
                      selectedValues={allUsers.filter(user => 
                        formData.members.some(member => member.user_id === user.id)
                      )}
                      onSelect={handleUserSelect}
                      onRemove={handleUserRemove}
                      displayValue="name"
                      id="associated_users"
                      customCloseIcon={
                        <img
                          style={{ width: "1rem", height: "1rem", marginLeft: "1rem" }}
                          src={x}
                          alt="close"
                        />
                      }
                    />
                  </div>

                  <div className="ms-2" onClick={handleAddUser} title="Add user">
                    <Pluseicon />
                  </div>
                </div>
              </div>
            
              <div className="col-6 mb-3">
                <Label htmlFor="Company Logo" className="form-label">Company Logo</Label>
                <input
                  ref={fileInputRef}
                  type="file"
                  className="form-control"
                  accept=".png,.jpeg,.jpg,.gif"
                  name="logo"
                  onChange={handleLogoChange}
                />

                {/* Show preview or link if logoPreview is available */}
                {logoPreview && (
                  <div className="mt-2 d-flex align-items-center">
                    <div className="bg-light p-2">
                      <a href={logoPreview} target="_blank" rel="noopener noreferrer">
                      {logoPreview.split("/").pop()}
                      </a>
                    </div>
                    <div className="bg-light p-2" style={{ cursor: 'pointer' }} onClick={handleRemoveLogo}>
                      <img style={{ width: "1rem", height: "1rem", marginLeft: "1rem" }} src={x} alt="close" />
                    </div>
                  </div>
                )}

                {logoError && <div className="text-danger mt-1">{logoError}</div>}
              </div>

              <div className="d-flex justify-content-end mt-3">
                <button
                  type="button"
                  className="btn btn-secondary me-2"
                  onClick={handleCancel}
                >
                  Cancel
                </button>
                <button type="submit" className="btn SaveBtn" disabled={loadSave}>
                  {loadSave ? (
                    <>
                      <div
                        className="spinner-border spinner-border-sm me-2 d-inline-block"
                        role="status"
                      ></div>
                      <span className="d-inline-block">
                        {formData.company_id ? "Updating..." : "Saving..."}
                      </span>
                    </>
                  ) : (
                    formData.company_id ? "Update" : "Save"
                  )}
                </button>
              </div>
              </div>
          </form>
        </div>
      </WindowHeightComponent>
      <UserModal
        show={showAddUserModal}
        onClose={handleCloseModal}
        onSave={handleSaveNewUser}
        context="partner"
      />
    </MainLayout>
  );
};
export default PartnerTab;         